'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonGroup", "children", "className", "ContentWrapper", "onOpenChange", "open"];
import PropTypes from 'prop-types';
import { cloneElement, createRef, Children, Component } from 'react';
import devLogger from 'react-utils/devLogger';
import classNames from 'classnames';
import { warnIfFragment } from '../utils/devWarnings';
import UIButtonGroup from './UIButtonGroup';
import Controllable from '../decorators/Controllable';
import SyntheticEvent from '../core/SyntheticEvent';
import { FloatingButtonGroupContextProvider } from '../context/FloatingButtonGroupContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultContentWrapper = ({
  children,
  open
}) => {
  const onlyChild = children && Children.only(children);
  warnIfFragment(onlyChild, 'UIFloatingButtonGroup');
  return /*#__PURE__*/_jsx("div", {
    className: "private-floating-button-group__children",
    children: onlyChild && /*#__PURE__*/cloneElement(onlyChild, {
      open
    })
  });
};
const maybeRenderButtonsWithClasses = (buttonGroup, open) => {
  if (process.env.NODE_ENV !== 'production') {
    if (buttonGroup.type !== UIButtonGroup) {
      devLogger.warn({
        message: 'UIFloatingButtonGroup: Component in buttonGroup must be a UIButtonGroup component.',
        key: 'UIFloatingButtonGroup: buttonGroup'
      });
    }
  }
  return /*#__PURE__*/cloneElement(buttonGroup, {
    className: classNames('private-floating-button-group__buttons', buttonGroup.props.className, !open && 'sr-only')
  });
};
class UIFloatingButtonGroup extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      activeDropdownParent: null,
      hasFocus: false,
      hasMouseover: false,
      hasPopover: false,
      open: false
    };
    this.wrapperRef = /*#__PURE__*/createRef();
    this.handleBlur = () => {
      this.setState({
        hasFocus: false
      });
    };
    this.handleFocus = () => {
      this.setState({
        hasFocus: true
      });
    };
    this.handleMouseEnter = () => {
      this.setState({
        hasMouseover: true
      });
    };
    this.handleMouseMove = () => {
      if (!this.state.hasMouseover) {
        this.setState({
          hasMouseover: true
        });
      }
    };
    this.handleMouseLeave = () => {
      this.setState({
        hasMouseover: false
      });
    };
    this.setActiveDropdownParent = el => {
      this.setState({
        activeDropdownParent: el
      });
    };
  }
  componentDidMount() {
    this._observer = new MutationObserver(() => {
      const hasPopover = !!this.wrapperRef.current.querySelector('[data-popover-id]');
      if (this.state.hasPopover !== hasPopover) {
        this.setState({
          hasPopover
        });
      }
    });
    this._observer.observe(this.wrapperRef.current, {
      attributes: true,
      subtree: true
    });
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      onOpenChange
    } = this.props;
    const {
      activeDropdownParent,
      hasFocus,
      hasMouseover,
      hasPopover
    } = this.state;
    // These state checks prevent infinite update loops when `open` is controlled.
    if (hasFocus !== prevState.hasFocus || hasMouseover !== prevState.hasMouseover || hasPopover !== prevState.hasPopover || activeDropdownParent !== prevState.activeDropdownParent) {
      const shouldBeOpen = hasFocus || hasMouseover || hasPopover;
      onOpenChange(SyntheticEvent(shouldBeOpen));

      // Focus the parent dropdown item when the popover closes (#9453)
      if (activeDropdownParent && hasPopover !== prevState.hasPopover) {
        activeDropdownParent.focus();
      }
    }
  }
  componentWillUnmount() {
    this._observer.disconnect();
  }
  render() {
    const _this$props = this.props,
      {
        buttonGroup,
        children,
        className,
        ContentWrapper,
        open
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
      ref: this.wrapperRef,
      className: classNames(className, 'private-floating-button__group'),
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      onMouseMove: this.handleMouseMove,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      children: [/*#__PURE__*/_jsx(FloatingButtonGroupContextProvider, {
        value: {
          setFloatingButtonGroupActiveEl: this.setActiveDropdownParent
        },
        children: maybeRenderButtonsWithClasses(buttonGroup, open)
      }), /*#__PURE__*/_jsx(ContentWrapper, {
        open: open,
        children: children
      })]
    }));
  }
}
UIFloatingButtonGroup.propTypes = {
  children: PropTypes.node,
  ContentWrapper: PropTypes.elementType,
  buttonGroup: PropTypes.node.isRequired,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool
};
UIFloatingButtonGroup.defaultProps = {
  ContentWrapper: DefaultContentWrapper
};
UIFloatingButtonGroup.displayName = 'UIFloatingButtonGroup';
export default Controllable(UIFloatingButtonGroup, ['open']);